/**
 * #978 — the note icon in a card header.
 *
 * Renders nothing outside a `CardNotesProvider` (see that file). Inside one it
 * is a badge-sized button in the `PinToggle` mould: it stops propagation so a
 * note click never opens the order ticket, pins, cancels or trades, and it
 * carries `data-lt2-owner` so no hotkey layer can treat it as a trade key.
 *
 * Outlined icon = no note; purple icon + dot = a SAVED note (from the
 * canonical record only — a draft never lights it).
 *
 * The same target can be on screen twice (its section and the Pinned strip);
 * each instance registers its own anchor, so the panel anchors to, and returns
 * focus to, the one that was clicked.
 */
import { useEffect, useId, useMemo, useRef } from 'react';
import { NotepadText } from 'lucide-react';
import { useStore } from 'zustand';
import type { CardNotesController } from '@/lib/cardNotes/cardNotesController';
import { targetKey, withScope, type CardNoteSubject } from '@/lib/cardNotes/types';
import { useCardNotes } from './CardNotesProvider';

export function CardNoteControl({ subject, size = 12 }: { subject: CardNoteSubject; size?: number }) {
  const notes = useCardNotes();
  if (!notes) return null;
  return <NoteButton controller={notes.controller} scope={notes.scope} subject={subject} size={size} />;
}

/** What the icon can honestly say about the stored note. */
type NoteIconState = 'unknown' | 'unavailable' | 'loading' | 'error' | 'empty' | 'saved';

const ICON_LABEL: Record<NoteIconState, string> = {
  unknown: 'Trader note (unavailable)',
  unavailable: 'Trader note (unavailable)',
  loading: 'Trader note (loading)',
  error: 'Trader note (couldn’t load)',
  empty: 'Trader note (empty)',
  saved: 'Trader note (saved)',
};

const ICON_TITLE: Record<NoteIconState, string> = {
  unknown: 'Notes unavailable — this card has no resolved account yet',
  unavailable: "Notes unavailable: this card's account could not be confirmed",
  loading: 'Loading trader note…',
  error: 'Couldn’t load this note — click to retry',
  empty: 'Add a trader note',
  saved: 'Trader note — view or edit',
};

function NoteButton({
  controller,
  scope,
  subject,
  size,
}: {
  controller: CardNotesController;
  scope: string | null;
  subject: CardNoteSubject;
  size: number;
}) {
  const anchorId = useId();
  const ref = useRef<HTMLButtonElement>(null);

  // Keyed on primitives: `subject` is usually a fresh literal each render, and
  // a new target identity would re-register (and re-read) on every tick.
  const id = subject.kind === 'order' ? subject.venueOrderId : subject.marketId;
  const outcome = subject.kind === 'position' ? subject.outcome : null;
  const target = useMemo(
    () => (scope == null ? null : withScope(subject, scope)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `subject` is covered by its primitives
    [scope, subject.kind, subject.venue, id, outcome],
  );
  const key = target ? targetKey(target) : null;

  // A failed or pending read must never pass for "no note": the trader would
  // believe nothing is written on a card that has one.
  const state = useStore(controller.store, (s): NoteIconState => {
    if (!key) return 'unknown';
    const e = s.entries[key];
    if (!e || e.status === 'loading') return 'loading';
    if (e.status === 'error') return 'error';
    if (e.status === 'unavailable') return 'unavailable';
    return e.record.note != null ? 'saved' : 'empty';
  });
  const saved = state === 'saved';
  const open = useStore(controller.store, (s) => key != null && s.editor?.key === key);

  useEffect(() => {
    if (!target || !ref.current) return;
    return controller.registerAnchor(anchorId, target, ref.current);
  }, [controller, anchorId, target]);

  return (
    <button
      ref={ref}
      type="button"
      data-lt2-owner=""
      data-card-note-control=""
      disabled={!target || state === 'unavailable'}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={ICON_LABEL[state]}
      title={ICON_TITLE[state]}
      onClick={(e) => {
        e.stopPropagation();
        if (!target) return;
        if (open) controller.requestClose();
        else controller.open(target, anchorId);
      }}
      className={`relative shrink-0 rounded p-1 transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        saved || open
          ? 'text-purple-400 hover:bg-purple-400/10'
          : state === 'error'
            ? 'text-amber-400 hover:bg-amber-400/10'
            : 'text-muted-foreground hover:bg-purple-400/10 hover:text-purple-400'
      } ${state === 'loading' ? 'opacity-60' : ''} ${open ? 'bg-purple-400/10' : ''}`}
    >
      <NotepadText size={size} aria-hidden="true" />
      {state === 'error' && (
        <span
          aria-hidden="true"
          className="absolute top-0 right-0 text-[9px] leading-none font-bold text-amber-400"
        >
          !
        </span>
      )}
      {saved && (
        <span
          aria-hidden="true"
          data-card-note-saved=""
          className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-purple-400"
        />
      )}
    </button>
  );
}
